import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Users, Copy, LogIn, Loader2, User, ArrowLeft } from 'lucide-react';
import { createMatch, joinMatch, rejoinMatch, getPendingMatch, clearPendingMatch, savePendingMatch, cleanupOldMatches, swapSeat, startMatch, transferHost, type MatchData, type PendingMatch } from '../services/matchmaking';
import { cn } from '../lib/utils';
import {
  createMultiplayerInviteUrl,
  emitLifecycleEventOnce,
  emitProductEvent,
  inviteShareEvent,
  multiplayerCreateEvent,
  multiplayerJoinEvent,
} from '../lib/productAnalytics';
import { useSettings } from './SettingsContext';

interface MenuOnlineProps {
  myUid: string;
  initialPlayerName?: string;
  onMatchCreated: (code: string) => void;
  onMatchJoined: (code: string) => void;
  onBack: () => void;
}

function getSharedInviteCode(): string {
  if (typeof window === 'undefined') return '';
  const inviteCode = new URLSearchParams(window.location.search).get('invite') || '';
  return /^\d{6}$/.test(inviteCode) ? inviteCode : '';
}

export default function MenuOnline({ myUid, initialPlayerName, onMatchCreated, onMatchJoined, onBack }: MenuOnlineProps) {
  const { tableTheme } = useSettings();
  const [playerName, setPlayerName] = useState(initialPlayerName || '');
  const [joinCode, setJoinCode] = useState(getSharedInviteCode);
  const [isCreating, setIsCreating] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingMatch, setPendingMatch] = useState<PendingMatch | null>(null);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [numPlayers, setNumPlayers] = useState<number>(2);
  const [targetScore, setTargetScore] = useState<number>(16);

  // Consume the share-only parameter after React has captured it. This keeps
  // private room codes out of copied browser history while preserving form state.
  React.useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('invite')) return;
    url.searchParams.delete('invite');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }, []);

  // Sync player name with profile name when loaded
  React.useEffect(() => {
    if (initialPlayerName && !playerName) {
      setPlayerName(initialPlayerName);
    }
  }, [initialPlayerName]);

  // Check for pending match on mount + cleanup old matches
  React.useEffect(() => {
    const pending = getPendingMatch();
    if (pending && pending.uid === myUid) {
      setPendingMatch(pending);
    }
    cleanupOldMatches(myUid);
  }, [myUid]);

  // When playerName is set, save it to pending match if one exists
  React.useEffect(() => {
    if (pendingMatch && playerName.trim()) {
      savePendingMatch({ ...pendingMatch, myName: playerName.trim() });
    }
  }, [playerName, pendingMatch]);

  if (!myUid) {
    return (
      <div 
        className={cn(
          "fixed inset-0 game-bg text-white font-sans select-none overflow-y-auto",
          `game-bg-${tableTheme}`
        )}
      >
        <div className="min-h-[100dvh] w-full flex flex-col items-center justify-start p-4 pb-8">
          <div className="w-full max-w-sm my-auto text-center space-y-4 py-8">
            <Loader2 className="w-8 h-8 animate-spin text-yellow-500 mx-auto" />
            <p className="text-white/80 font-medium">Connessione al server in corso...</p>
            <p className="text-white/40 text-xs leading-relaxed">
              Se questa schermata persiste, verifica la tua connessione a Internet o assicurati che l'accesso anonimo sia abilitato nella console di Firebase.
            </p>
            <div className="pt-2">
              <button
                onClick={onBack}
                className="px-6 py-2 bg-white/10 hover:bg-white/20 border border-white/10 hover:border-white/30 rounded-xl transition-all text-sm font-semibold"
              >
                Torna al Menu
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleCreateMatch = async () => {
    if (!playerName.trim()) {
      setError('Inserisci il tuo nome prima di creare una partita.');
      return;
    }
    setIsCreating(true);
    setError(null);
    try {
      const code = await createMatch(myUid, playerName.trim(), numPlayers, targetScore);
      savePendingMatch({ matchId: code, uid: myUid, isHost: true, myName: playerName.trim(), timestamp: Date.now() });
      emitLifecycleEventOnce(multiplayerCreateEvent(code, numPlayers, targetScore));
      onMatchCreated(code);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Errore nella creazione della partita: ${msg}`);
      console.error('[MenuOnline] createMatch error:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleJoinMatch = async () => {
    console.log('[MenuOnline] handleJoinMatch called', { joinCode, myUid, playerName, joinCodeLen: joinCode.length });
    if (!playerName.trim()) {
      setError('Inserisci il tuo nome prima di unirti a una partita.');
      return;
    }
    if (joinCode.length !== 6) {
      setError('Inserisci un codice di 6 cifre.');
      return;
    }
    setIsJoining(true);
    setError(null);
    const normalizedCode = joinCode.toUpperCase();
    try {
      const result = await joinMatch(normalizedCode, myUid, playerName.trim());
      if (result.success) {
        savePendingMatch({ matchId: normalizedCode, uid: myUid, isHost: false, myName: playerName.trim(), timestamp: Date.now() });
        emitLifecycleEventOnce(multiplayerJoinEvent(normalizedCode, 'new'));
        onMatchJoined(normalizedCode);
      } else if (result.reason === 'already_member') {
        // Player is already in this match (disconnected seat) — try to rejoin
        const rejoined = await rejoinMatch(normalizedCode, myUid, playerName.trim());
        if (rejoined) {
          savePendingMatch({ matchId: normalizedCode, uid: myUid, isHost: false, myName: playerName.trim(), timestamp: Date.now() });
          emitLifecycleEventOnce(multiplayerJoinEvent(normalizedCode, 'rejoin'));
          onMatchJoined(normalizedCode);
        } else {
          setError('Impossibile riconnettersi. La partita potrebbe essere terminata.');
        }
      } else if (result.reason === 'not_found') {
        setError('Partita non trovata.');
      } else if (result.reason === 'full') {
        setError('Partita già piena.');
      } else if (result.reason === 'wrong_status') {
        setError('Partita non più disponibile.');
      } else {
        setError('Errore sconosciuto.');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Errore durante la connessione: ${msg}`);
      console.error('[MenuOnline] joinMatch error:', err);
    } finally {
      setIsJoining(false);
    }
  };

  const handleResumeMatch = async () => {
    if (!pendingMatch) return;
    setResumeLoading(true);
    setError(null);

    try {
      const rejoined = await rejoinMatch(pendingMatch.matchId, myUid, playerName.trim());
      if (!rejoined) {
        clearPendingMatch();
        setPendingMatch(null);
        setError('La partita in sospeso non è più disponibile.');
        return;
      }

      const refreshedPending = {
        ...pendingMatch,
        myName: playerName.trim(),
        timestamp: Date.now(),
      };
      savePendingMatch(refreshedPending);
      setPendingMatch(refreshedPending);
      emitLifecycleEventOnce(multiplayerJoinEvent(pendingMatch.matchId, 'rejoin'));

      if (pendingMatch.isHost) {
        onMatchCreated(pendingMatch.matchId);
      } else {
        onMatchJoined(pendingMatch.matchId);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Errore durante la riconnessione: ${msg}`);
      console.error('[MenuOnline] resumeMatch error:', err);
    } finally {
      setResumeLoading(false);
    }
  };

  const handleDismissPending = () => {
    clearPendingMatch();
    setPendingMatch(null);
  };

  return (
    <div 
      className={cn(
        "fixed inset-0 game-bg text-white font-sans select-none overflow-y-auto",
        `game-bg-${tableTheme}`
      )}
    >
      <div className="min-h-[100dvh] w-full flex flex-col items-center justify-start p-4 pb-8">
        {/* Back button bar */}
        <div 
          className="w-full max-w-md flex justify-start pt-2 z-10 shrink-0"
          style={{ paddingTop: "calc(env(safe-area-inset-top) + 0.5rem)" }}
        >
          <button
            onClick={onBack}
            className="p-2.5 glass-panel rounded-xl transition-all hover:scale-105 active:scale-95"
          >
            <ArrowLeft className="w-5 h-5 text-white/60" />
          </button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md my-auto py-6 space-y-5 flex flex-col"
        >
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center">
              <Users className="w-8 h-8 text-yellow-400" />
            </div>
          </div>
          <h2 className="text-3xl font-black text-yellow-400" style={{ fontFamily: '"Playfair Display", serif' }}>
            Multiplayer Online
          </h2>
          <p className="text-white/50 text-sm">Sfida un amico a distanza</p>
        </div>

        {/* Player Name Input */}
        <div className="space-y-2">
          <label className="text-white/60 text-xs uppercase tracking-widest font-bold">Il tuo nome</label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
            <input
              type="text"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value.slice(0, 20))}
              placeholder="Nome giocatore"
              maxLength={20}
              className="w-full py-4 pl-12 pr-4 glass-panel rounded-2xl text-white placeholder:text-white/20 focus:outline-none focus:border-yellow-500/50"
            />
          </div>
        </div>

        {/* Player Count Selector */}
        <div className="space-y-2">
          <label className="text-white/60 text-xs uppercase tracking-widest font-bold">Numero di giocatori</label>
          <div className="grid grid-cols-3 gap-2 glass-panel p-1.5 rounded-2xl">
            {[2, 3, 4].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setNumPlayers(n)}
                className={cn(
                  "py-2.5 rounded-xl font-bold text-sm transition-all",
                  numPlayers === n
                    ? "bg-yellow-500 text-black shadow-lg shadow-yellow-500/20"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                )}
              >
                {n} Giocatori
              </button>
            ))}
          </div>
        </div>

        {/* Target Score Selector */}
        <div className="space-y-2">
          <label className="text-white/60 text-xs uppercase tracking-widest font-bold">Punti obiettivo</label>
          <div className="grid grid-cols-3 gap-2 glass-panel p-1.5 rounded-2xl">
            {[16, 21, 31].map((score) => (
              <button
                key={score}
                type="button"
                onClick={() => setTargetScore(score)}
                className={cn(
                  "py-2.5 rounded-xl font-bold text-sm transition-all",
                  targetScore === score
                    ? "bg-yellow-500 text-black shadow-lg shadow-yellow-500/20"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                )}
              >
                {score} Punti
              </button>
            ))}
          </div>
        </div>

        {/* Pending Match Banner */}
        {pendingMatch && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-yellow-500/20 border border-yellow-500/40 rounded-2xl p-4 space-y-3"
          >
            <p className="text-yellow-300 text-sm font-bold text-center">
              Partita in sospeso trovata
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleResumeMatch}
                disabled={resumeLoading || !playerName.trim()}
                className="w-full py-3 px-4 bg-yellow-500/30 hover:bg-yellow-500/50 border border-yellow-500/50 rounded-xl text-yellow-300 font-bold text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {resumeLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Users className="w-4 h-4" />
                )}
                {resumeLoading ? 'Connessione...' : `Riprendi Partita (${pendingMatch.isHost ? 'Host' : 'Guest'})`}
              </button>
              <button
                onClick={handleDismissPending}
                className="w-full py-2 text-white/40 hover:text-white/70 text-xs font-medium transition-colors"
              >
                No, inizia nuova partita
              </button>
            </div>
          </motion.div>
        )}

        {/* Error */}
        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-red-600/80 text-white text-sm font-bold px-4 py-3 rounded-xl text-center"
          >
            {error}
          </motion.div>
        )}

        {/* Create Match */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleCreateMatch}
          disabled={isCreating || isJoining || !playerName.trim()}
          className={cn(
            "w-full py-4 px-6 glass-panel hover:glass-panel-amber rounded-2xl transition-all flex items-center justify-center gap-3",
            (isCreating || !playerName.trim()) && "opacity-50 cursor-not-allowed"
          )}
        >
          {isCreating ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Users className="w-5 h-5 text-yellow-400" />
          )}
          <span className="text-xl font-black">
            {isCreating ? 'Creando partita...' : 'Crea Partita'}
          </span>
        </motion.button>

        {/* Divider */}
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-white/30 text-xs uppercase tracking-widest">oppure</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        {/* Join with Code */}
        <div className="space-y-3">
          <div className="relative">
            <input
              type="text"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Codice partita"
              maxLength={6}
              className="w-full py-4 px-6 glass-panel rounded-2xl text-center text-2xl font-mono tracking-[0.3em] placeholder:text-white/20 focus:outline-none focus:border-yellow-500/50"
            />
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleJoinMatch}
            disabled={isCreating || isJoining || joinCode.length !== 6 || !playerName.trim()}
            className={cn(
              "w-full py-4 px-6 glass-panel hover:glass-panel-rose rounded-2xl transition-all flex items-center justify-center gap-3",
              (isJoining || joinCode.length !== 6 || !playerName.trim()) && "opacity-50 cursor-not-allowed"
            )}
          >
            {isJoining ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <LogIn className="w-5 h-5 text-green-400" />
            )}
            <span className="text-xl font-black">
              {isJoining ? 'Unisciti...' : 'Unisciti con Codice'}
            </span>
          </motion.button>
        </div>

        {/* Back Button */}
        <button
          onClick={onBack}
          className="w-full py-3 text-white/40 hover:text-white/70 text-sm font-medium transition-colors"
        >
          ← Torna al menu
        </button>
      </motion.div>
      </div>
    </div>
  );
}

// Dots animation component
function DotsAnimation() {
  const [dotCount, setDotCount] = React.useState(1);
  React.useEffect(() => {
    const interval = setInterval(() => {
      setDotCount(prev => prev >= 3 ? 1 : prev + 1);
    }, 500);
    return () => clearInterval(interval);
  }, []);
  return (
    <span className="inline-block w-8 text-left text-yellow-400 font-bold">
      {'.'.repeat(dotCount)}
    </span>
  );
}

// Waiting Room Component
interface WaitingRoomProps {
  code: string;
  isHost: boolean;
  match: MatchData | null;
  myUid: string;
  onCancel: () => void;
}

function getSeatLabel(index: number, numPlayers: number): string {
  if (numPlayers === 4) {
    return index % 2 === 0 ? `Posto ${index + 1} (Coppia A)` : `Posto ${index + 1} (Coppia B)`;
  }
  return index === 0 ? `Posto 1 (Host)` : `Posto ${index + 1}`;
}

export function WaitingRoom({ code, isHost, match, myUid, onCancel }: WaitingRoomProps) {
  const [copiedCode, setCopiedCode] = React.useState(false);
  const [copiedLink, setCopiedLink] = React.useState(false);
  const [copyError, setCopyError] = React.useState<string | null>(null);
  const [claimingHost, setClaimingHost] = React.useState(false);

  const copyCodeToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopyError(null);
      setCopiedCode(true);
      emitProductEvent(inviteShareEvent('code'));
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      console.warn('[WaitingRoom] Unable to copy match code:', error);
      setCopyError('Impossibile copiare il codice.');
    }
  };

  const copyLinkToClipboard = async () => {
    const inviteUrl = createMultiplayerInviteUrl(window.location.origin, code);
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopyError(null);
      setCopiedLink(true);
      emitProductEvent(inviteShareEvent('link'));
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (error) {
      console.warn('[WaitingRoom] Unable to copy invite link:', error);
      setCopyError('Impossibile copiare il link di invito.');
    }
  };

  const handleClaimHost = async () => {
    setClaimingHost(true);
    try {
      await transferHost(code, myUid);
    } finally {
      setClaimingHost(false);
    }
  };

  // Check if host seat is empty/disconnected and there are other players
  const hostPlayer = match?.players.find(p => p.uid === match.hostUid);
  const hostDisconnected = !hostPlayer || hostPlayer.uid === '' || hostPlayer.disconnected === true;
  const hasOtherPlayers = match && match.players.some(p => p.uid !== '' && p.uid !== myUid);
  const canClaimHost = !isHost && hostDisconnected && hasOtherPlayers;

  return (
    <div 
      className="fixed inset-0 text-white font-sans select-none overflow-y-auto"
      style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}
    >
      <div className="min-h-[100dvh] w-full flex flex-col items-center justify-start p-4 pb-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          className="w-full max-w-md my-auto py-4 text-center space-y-5 flex flex-col"
        >
        {/* Logo SBARAZZO */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
          className="space-y-1"
        >
          <h1
            className="text-4xl sm:text-5xl font-black text-yellow-400 tracking-wider"
            style={{ fontFamily: '"Playfair Display", serif', textShadow: '0 0 40px rgba(234, 179, 8, 0.3)' }}
          >
            SBARAZZINO
          </h1>
          <p className="text-white/30 text-[10px] uppercase tracking-[0.4em]">Multiplayer</p>
        </motion.div>

        {/* Share Code Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="glass-panel rounded-3xl p-5 space-y-3"
        >
          <p className="text-white/40 text-[10px] uppercase tracking-[0.3em] font-bold">Codice partita</p>
          <div className="flex items-center justify-center gap-4">
            <motion.span
              key={code}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-4xl sm:text-5xl font-mono tracking-[0.25em] text-yellow-400 font-black"
              style={{ fontFamily: '"Playfair Display", serif', letterSpacing: '0.25em' }}
            >
              {code}
            </motion.span>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={copyCodeToClipboard}
              className="p-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all"
              title="Copia codice"
            >
              {copiedCode ? (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="text-green-400"
                >
                  <Copy className="w-5 h-5" />
                </motion.div>
              ) : (
                <Copy className="w-5 h-5 text-white/50" />
              )}
            </motion.button>
          </div>
          {copiedCode && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-green-400 text-xs font-medium"
            >
              Codice copiato!
            </motion.p>
          )}

          <div className="pt-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={copyLinkToClipboard}
              className="w-full py-2.5 bg-yellow-500 hover:bg-yellow-400 text-black rounded-xl font-bold text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2"
            >
              <Copy className="w-4 h-4" />
              {copiedLink ? 'Link Copiato!' : 'Copia Link Invito'}
            </motion.button>
          </div>
          {copyError && <p className="text-red-400 text-xs font-medium">{copyError}</p>}
        </motion.div>

        {/* Seat Selection list */}
        {match && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-panel rounded-3xl p-5 space-y-4 text-left"
          >
            <div className="flex justify-between items-center pb-2 border-b border-white/5">
              <span className="text-white/40 text-xs uppercase tracking-[0.3em] font-bold">Posti disponibili</span>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 px-2 py-0.5 rounded-full font-bold">
                  Traguardo: {match.targetScore ?? 16} Punti
                </span>
                <span className="text-[10px] bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 px-2 py-0.5 rounded-full font-bold">
                  {match.numPlayers} Giocatori
                </span>
              </div>
            </div>

            <div className="grid gap-2.5">
              {match.players.slice(0, match.numPlayers).map((player, idx) => {
                const isOccupied = player.uid !== '';
                const isMeInThisSeat = player.uid === myUid;
                const isThisHost = match.hostUid === player.uid;
                return (
                  <div
                    key={idx}
                    className={cn(
                      "flex items-center justify-between p-3.5 rounded-2xl border transition-all duration-300",
                      isMeInThisSeat
                        ? "glass-panel-amber active-glow-panel scale-[1.01]"
                        : isOccupied
                        ? "bg-white/5 border-white/10"
                        : "bg-white/5 border-dashed border-white/15 hover:border-white/30"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-2.5 h-2.5 rounded-full transition-colors duration-300",
                        isMeInThisSeat ? "bg-yellow-400 animate-pulse" : isOccupied ? "bg-green-500" : "bg-white/10"
                      )} />
                      <div>
                        <div className="text-[10px] text-white/40 font-bold uppercase tracking-wider">
                          {getSeatLabel(idx, match.numPlayers)}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className={cn("text-sm sm:text-base font-bold transition-all", isOccupied ? "text-white" : "text-white/20 italic")}>
                            {isOccupied ? (player.uid === myUid ? `${player.name} (Tu)` : player.name) : "Libero"}
                          </div>
                          {isThisHost && (
                            <span className="text-[8px] bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 px-1.5 py-0.5 rounded-full font-bold uppercase">Host</span>
                          )}
                          {isOccupied && player.disconnected && (
                            <span className="text-[8px] bg-red-500/20 text-red-400 border border-red-500/30 px-1.5 py-0.5 rounded-full font-bold uppercase animate-pulse">Disconnesso</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {!isMeInThisSeat && !isOccupied && (
                      <button
                        onClick={async () => {
                          await swapSeat(code, myUid, idx);
                        }}
                        className="py-1.5 px-3 glass-panel hover:glass-panel-amber rounded-xl text-xs font-bold transition-all text-white/80 active:scale-95"
                      >
                        Siediti qui
                      </button>
                    )}

                    {/* Claim Host button shown when host disconnected */}
                    {canClaimHost && !isOccupied && idx === 0 && (
                      <button
                        onClick={handleClaimHost}
                        disabled={claimingHost}
                        className="py-1.5 px-3 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/40 rounded-xl text-xs font-bold transition-all text-yellow-400 active:scale-95 disabled:opacity-50"
                      >
                        {claimingHost ? <Loader2 className="w-3 h-3 animate-spin inline" /> : 'Diventa Host'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Host Start Match Button (enabled only when all seats are full and numPlayers > 2) */}
            {isHost && match.numPlayers > 2 && (
              <div className="pt-2">
                <button
                  onClick={async () => {
                    await startMatch(code, myUid);
                  }}
                  disabled={!match.players.slice(0, match.numPlayers).every(p => p.uid !== '')}
                  className={cn(
                    "w-full py-4 text-black font-black uppercase text-sm tracking-widest rounded-2xl shadow-xl transition-all duration-300 active:scale-98",
                    match.players.slice(0, match.numPlayers).every(p => p.uid !== '')
                      ? "bg-yellow-500 hover:bg-yellow-400 shadow-yellow-500/10 cursor-pointer"
                      : "bg-white/5 text-white/20 border border-white/5 cursor-not-allowed"
                  )}
                >
                  Avvia Partita
                </button>
                {!match.players.slice(0, match.numPlayers).every(p => p.uid !== '') && (
                  <p className="text-white/30 text-xs mt-2 text-center">In attesa che tutti i giocatori prendano posto</p>
                )}
              </div>
            )}

            {/* Waiting for Host start message (for guests) */}
            {!isHost && match.numPlayers > 2 && (
              <div className="pt-2 text-center text-xs text-white/40 font-medium flex items-center justify-center gap-1.5 bg-black/20 rounded-xl py-3 border border-white/5">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-yellow-400" />
                In attesa che l'host avvii la partita<DotsAnimation />
              </div>
            )}
          </motion.div>
        )}

        {/* Cancel Button */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onCancel}
          className="py-3 px-6 text-white/40 hover:text-white/70 text-sm font-medium transition-colors border border-white/10 hover:border-white/20 rounded-full w-full"
        >
          Annulla e torna al menu
        </motion.button>
      </motion.div>
      </div>
    </div>
  );
}
